import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rfqApi, type RFQ, type CreateRFQDto, type SendRFQDto, type AwardRFQDto } from '../../../api/rfq';
import { poApi } from '../../../api/po';
import { notifications } from '@mantine/notifications';

export function useRFQs(filters?: { status?: RFQ['status']; category?: RFQ['category']; search?: string }) {
  return useQuery({
    queryKey: ['rfqs', filters],
    queryFn: () => rfqApi.list(filters),
  });
}

export function useRFQ(id: string | undefined) {
  return useQuery({
    queryKey: ['rfq', id],
    queryFn: () => rfqApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateRFQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRFQDto) => rfqApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
      notifications.show({
        title: 'Success',
        message: 'RFQ created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create RFQ',
        color: 'red',
      });
    },
  });
}

export function useUpdateRFQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateRFQDto }) => rfqApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['rfq', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'RFQ updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update RFQ',
        color: 'red',
      });
    },
  });
}

export function useSendRFQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SendRFQDto }) => rfqApi.send(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['rfq', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'RFQ sent to vendors successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to send RFQ',
        color: 'red',
      });
    },
  });
}

export function useAwardRFQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AwardRFQDto }) => rfqApi.award(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['rfq', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'RFQ awarded successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to award RFQ',
        color: 'red',
      });
    },
  });
}

export function useRFQComparison(id: string | undefined) {
  return useQuery({
    queryKey: ['rfq-comparison', id],
    queryFn: () => rfqApi.getComparison(id!),
    enabled: !!id,
  });
}

export function useSelectVendors() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rules }: { id: string; rules: any }) => rfqApi.selectVendors(id, rules),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfq', variables.id] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to select vendors',
        color: 'red',
      });
    },
  });
}

export function useSubmitRFQForApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => rfqApi.submitForApproval(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['rfq', id] });
      notifications.show({
        title: 'Success',
        message: 'RFQ submitted for approval',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to submit RFQ for approval',
        color: 'red',
      });
    },
  });
}

export function useApproveRFQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comments }: { id: string; comments?: string }) =>
      rfqApi.approve(id, comments),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['rfq', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'RFQ approved successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to approve RFQ',
        color: 'red',
      });
    },
  });
}

export function useRejectRFQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comments }: { id: string; comments: string }) =>
      rfqApi.reject(id, comments),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['rfq', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'RFQ rejected',
        color: 'yellow',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to reject RFQ',
        color: 'red',
      });
    },
  });
}

export function useGeneratePO() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rfqId, quotationId }: { rfqId: string; quotationId: string }) =>
      poApi.generateDraft(rfqId, quotationId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfq', variables.rfqId] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      notifications.show({
        title: 'Success',
        message: 'Purchase Order generated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to generate Purchase Order',
        color: 'red',
      });
    },
  });
}

